import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Offcanvas, Form, Row, Col } from 'react-bootstrap';
import $ from 'jquery';
import 'datatables.net-bs5';
import "datatables.net-responsive/js/dataTables.responsive.min.js";

function SalesOrderTable({ orders }) {
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);
    const [searchParams] = useSearchParams();
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        buyer: '',
        broker: '',
        status: '',
        orderNumber: '',
        dateFrom: '',
        dateTo: '',
        product: '',
        isInHouse: ''
    });
    const [products, setProducts] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [buyers, setBuyers] = useState([]);
    const [brokers, setBrokers] = useState([]);

    useEffect(() => {
        const productId = searchParams.get("product");
        if (productId) {
            setFilters(prev => ({ ...prev, product: productId }));
            const filtered = orders.filter(order =>
                order.items?.some(item => item.product?._id === productId)
            );
            setFilteredOrders(filtered);
        } else {
            setFilteredOrders(orders);
        }
    }, [searchParams, orders]);

    useEffect(() => {
        if (orders && orders.length > 0) {
            setFilteredOrders(orders);
            const uniqueBuyers = orders.filter(o => o.buyer).map(o => o.buyer);
            const uniqueBrokers = orders.filter(o => o.broker).map(o => o.broker);

            const uniqueProducts = orders
                .flatMap(o => o.items || [])
                .filter(i => i.product)
                .map(i => i.product);

            setProducts([...new Map(uniqueProducts.map(p => [p._id, p])).values()]);

            setBuyers([...new Map(uniqueBuyers.map(b => [b._id, b])).values()]);
            setBrokers([...new Map(uniqueBrokers.map(b => [b._id, b])).values()]);
        }
    }, [orders]);

    useEffect(() => {
        if (filteredOrders && filteredOrders.length >= 0) {
            const $table = $(tableRef.current);

            if (dataTableRef.current) {
                dataTableRef.current.destroy();
                dataTableRef.current = null;
            }

            setTimeout(() => {
                dataTableRef.current = $table.DataTable({
                    responsive: true,
                    destroy: true,
                    data: filteredOrders,
                    columns: [
                        {
                            data: null,
                            render: (data, type, row, meta) => meta.row + 1
                        },
                        {
                            data: null,
                            render: (data, type, row, meta) => {
                                let displayData = row.orderNumber || `Draft SO`;
                                let html = `<a href="/sales-orders/${row._id}">${displayData}</a>`;
                                if (row.isInHouseOrder) {
                                    html += ` <p class="small mb-0"><span class="badge bg-success"><i class="fas fa-building me-1"></i>In-House</span></p>`;
                                }
                                return html;
                            }
                        },
                        {
                            data: 'orderDate',
                            render: (data) => new Date(data).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                            })
                        },
                        {
                            data: 'items',
                            render: (data) => {
                                if (!data || data.length === 0) return 'No items';
                                return data.map(item =>
                                    `${item.product?.productName || 'N/A'} (${item.quantity} ${item.unit})`
                                ).join('<br>');
                            },
                            orderable: false
                        },
                        {
                            data: null,
                            render: (data, type, row) => {
                                let html = `<div>${row.buyer?.business_name || 'N/A'}</div>`;
                                if (row.broker?.business_name) {
                                    html += `<small class="text-muted">via: ${row.broker.business_name}</small>`;
                                }
                                return html;
                            }
                        },
                        {
                            data: 'status',
                            render: (data) => `<span class="badge text-bg-${getOrderStatusColor(data)}">${data}</span>`
                        },
                        {
                            data: 'EDD',
                            render: (data) => new Date(data).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                            })
                        },
                        {
                            data: '_id',
                            render: (data) => `<a class="btn btn-sm btn-info" href="/sales-orders/${data}">View</a>`,
                            className: 'text-end',
                            orderable: false
                        }
                    ],
                    layout: {
                        bottomEnd: {
                            paging: {
                                firstLast: false
                            }
                        }
                    }
                });
            }, 100);
        }

        return () => {
            if (dataTableRef.current) {
                dataTableRef.current.destroy();
                dataTableRef.current = null;
            }
        };
    }, [filteredOrders]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const filterOrders = (ordersList, filters) => {
        let filtered = ordersList || [];

        if (filters.buyer) {
            filtered = filtered.filter(order => order.buyer?._id === filters.buyer);
        }
        if (filters.broker) {
            filtered = filtered.filter(order => order.broker?._id === filters.broker);
        }
        if (filters.status) {
            filtered = filtered.filter(order => order.status === filters.status);
        }
        if (filters.orderNumber) {
            filtered = filtered.filter(order =>
                order.orderNumber?.toLowerCase().includes(filters.orderNumber.toLowerCase())
            );
        }
        if (filters.dateFrom) {
            filtered = filtered.filter(order =>
                new Date(order.orderDate) >= new Date(filters.dateFrom)
            );
        }
        if (filters.dateTo) {
            filtered = filtered.filter(order =>
                new Date(order.orderDate) <= new Date(filters.dateTo)
            );
        }
        if (filters.product) {
            filtered = filtered.filter(order =>
                order.items?.some(item => item.product?._id === filters.product)
            );
        }
        if (filters.isInHouse !== '') {
            const isInHouseFilter = filters.isInHouse === 'true';
            filtered = filtered.filter(order => order.isInHouseOrder === isInHouseFilter);
        }

        return filtered;
    };

    const applyFilters = () => {
        const filtered = filterOrders(orders, filters);
        setFilteredOrders(filtered);
        setShowFilters(false);
    };

    // Auto apply when filters change (URL or UI)
    useEffect(() => {
        const filtered = filterOrders(orders, filters);
        setFilteredOrders(filtered);
    }, [filters, orders]);

    const clearFilters = () => {
        setFilters({
            buyer: '',
            broker: '',
            status: '',
            orderNumber: '',
            dateFrom: '',
            dateTo: '',
            product: '',
            isInHouse: ''
        });
        setFilteredOrders(orders || []);
    };

    if (!orders || orders.length === 0) {
        return <p>No sales order found.</p>;
    }

    return (
        <>
            <div className="d-flex justify-content-between mb-3">
                <div>
                    <small className="text-muted">
                        Showing {filteredOrders.length} of {orders?.length || 0} orders
                    </small>
                </div>
                <Button variant="outline-primary" onClick={() => setShowFilters(true)}>
                    <i className="fas fa-filter me-2"></i>Filters
                </Button>
            </div>

            <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="end">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Filter Sales Orders</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Order Number</Form.Label>
                            <Form.Control
                                type="text"
                                value={filters.orderNumber}
                                onChange={(e) => handleFilterChange('orderNumber', e.target.value)}
                                placeholder="Search order number..."
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Product</Form.Label>
                            <Form.Select
                                value={filters.product}
                                onChange={(e) => handleFilterChange('product', e.target.value)}
                            >
                                <option value="">All Products</option>
                                {products.map(product => (
                                    <option key={product._id} value={product._id}>
                                        {product.productName}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Buyer</Form.Label>
                            <Form.Select
                                value={filters.buyer}
                                onChange={(e) => handleFilterChange('buyer', e.target.value)}
                            >
                                <option value="">All Buyers</option>
                                {buyers.map(buyer => (
                                    <option key={buyer._id} value={buyer._id}>
                                        {buyer.business_name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Broker</Form.Label>
                            <Form.Select
                                value={filters.broker}
                                onChange={(e) => handleFilterChange('broker', e.target.value)}
                            >
                                <option value="">All Brokers</option>
                                {brokers.map(broker => (
                                    <option key={broker._id} value={broker._id}>
                                        {broker.business_name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="">All Statuses</option>
                                <option value="Cancelled">Cancelled</option>
                                <option value="Pending">Pending</option>
                                <option value="Loaded">Loaded</option>
                                <option value="Delivered">Delivered</option>
                                <option value="On Hold">On Hold</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Order Type</Form.Label>
                            <Form.Select
                                value={filters.isInHouse}
                                onChange={(e) => handleFilterChange('isInHouse', e.target.value)}
                            >
                                <option value="">All Orders</option>
                                <option value="true">In-House Orders Only</option>
                                <option value="false">External Orders Only</option>
                            </Form.Select>
                        </Form.Group>

                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date From</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={filters.dateFrom}
                                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date To</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={filters.dateTo}
                                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-grid gap-2">
                            <Button variant="primary" onClick={applyFilters}>
                                Apply Filters
                            </Button>
                            <Button variant="secondary" onClick={clearFilters}>
                                Clear Filters
                            </Button>
                        </div>
                    </Form>
                </Offcanvas.Body>
            </Offcanvas>

            <table
                ref={tableRef}
                className="table table-bordered table-hover table-sm display responsive nowrap"
                id="ordersTable"
            >
                <thead>
                    <tr>
                        <th>SN</th>
                        <th>Order No</th>
                        <th>Order Date</th>
                        <th>Items</th>
                        <th>Buyer</th>
                        <th>Status</th>
                        <th>EDD</th>
                        <th className="text-end">Action</th>
                    </tr>
                </thead>
                <tbody>
                </tbody>
            </table>
        </>
    );
}

function getOrderStatusColor(status) {
    switch (status) {
        case 'Draft':
            return 'warning text-dark';
        case 'Created':
            return 'primary';
        case 'Confirmed':
            return 'info';
        case 'In Production':
            return 'info';
        case 'Ready for Dispatch':
            return 'success';
        case 'Partially Dispatched':
            return 'warning';
        case 'Fully Dispatched':
        case 'Dispatched':
            return 'success';
        case 'Delivered':
            return 'success';
        case 'Cancelled':
            return 'danger';
        case 'On Hold':
            return 'danger';
        default:
            return 'light text-dark';
    }
}

export default SalesOrderTable;
